/**
 * "Needs attention" (2026-09-28): every project with a real warning sign —
 * over budget, late, overdue or blocked work, open issues, gone quiet — and
 * the plain reason for each. Backend: app/services/portfolio_health.py.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, CheckCircle2, ChevronRight } from 'lucide-react'

import { portfolioHealthService, type NeedsAttention } from '../../services/portfolioHealth'

const LEVEL = {
  at_risk: { label: 'At risk', cls: 'bg-red-100 text-red-800' },
  watch: { label: 'Watch', cls: 'bg-amber-100 text-amber-800' },
} as const

export function NeedsAttentionCard({ limit = 8 }: { limit?: number }) {
  const navigate = useNavigate()
  const [data, setData] = useState<NeedsAttention | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    portfolioHealthService.needsAttention(limit).then(setData).catch(() => setFailed(true))
  }, [limit])

  if (failed) return null

  return (
    <section className="rounded-xl border border-border bg-card p-4 sm:p-6" aria-labelledby="needs-attention-heading">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="needs-attention-heading" className="flex items-center gap-2 text-lg font-semibold text-foreground">
          <AlertTriangle className="h-5 w-5 text-amber-600" aria-hidden /> Needs attention
        </h2>
        {data && (
          <p className="text-sm text-muted-foreground">
            {data.counts.at_risk} at risk · {data.counts.watch} to watch · {data.counts.checked} project(s) checked
          </p>
        )}
      </div>
      {!data ? (
        <p className="text-sm text-muted-foreground">Checking your projects…</p>
      ) : data.projects.length === 0 ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden /> Nothing needs attention right now.
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {data.projects.map((p) => (
            <li key={p.project_id}>
              <button type="button" onClick={() => navigate(`/project/${p.project_id}`)}
                className="flex w-full items-start justify-between gap-3 py-3 text-left hover:bg-muted/50 min-h-[44px]">
                <span className="space-y-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-foreground">{p.project_name}</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${LEVEL[p.level].cls}`}>{LEVEL[p.level].label}</span>
                    <span className="text-xs text-muted-foreground">{p.progress_percent}% done</span>
                  </span>
                  <span className="block text-sm text-muted-foreground">
                    {p.reasons.map((r) => r.text).join(' ')}
                  </span>
                </span>
                <ChevronRight className="mt-1 h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
